import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Output, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { GridAllModule } from '@syncfusion/ej2-angular-grids';
import { TabModule } from '@syncfusion/ej2-angular-navigations';
import { Observable, Subscription } from 'rxjs';
import { StandardReportService, AuthorisedReportDataResponse } from '../../../../../src/app/service/standard-report.service';
import { UserService } from '../../../../../src/app/service/user.service';
import { setStandardReportTab } from '../../../../../src/app/state-files/action';
import { selectStandardReportStreams } from '../../../../../src/app/state-files/standard-report.streams';
import { detailReportAlert, formatReportDate, openShowAllStandardReport, requestedReportParams } from '../../../../../src/app/shared/s-standard-report';
import { selectIsStandardReportStateFresh, selectStandardReportClParameterValue, selectStandardReportDateState, selectStandardReportEndDate, selectStandardReportLocationDropdownData, selectStandardReportLocationState, selectStandardReportSelectedLocation, selectStandardReportShowLocationDropdown, selectStandardReportStartDate, selectStandardReportTabIndex } from '../../../../../src/app/state-files/selectors';
import { AlertPopupComponent } from '../../../../components/alert-popup/alert-popup.component';
import { ViewTempDocumentsComponent } from '../../../../components/view-temp-documents/view-temp-documents.component';
import { buildReportViewerNavigation } from '../report-viewer-navigation';
import { buildWeeklyReportParams } from './weekly-report-request';
import { saveReportDownload } from '../report-download';
import { resolveReportDateRange, resolveReportViewerConfig } from '../report-viewer-response';

@Component({
  selector: 'app-weekly-report',
  standalone: true,
  imports: [CommonModule, TabModule, DatePickerModule, DropDownListModule, GridAllModule, FormsModule, TranslateModule, AlertPopupComponent, ViewTempDocumentsComponent],
  templateUrl: './weekly-report.component.html',
  styleUrl: './weekly-report.component.css',
})
export class WeeklyReportComponent {
  /** Ends this page's load requests when it is destroyed. */
  private readonly destroyRef = inject(DestroyRef);

  maxDate: Date = new Date();
  startDate: Date | null = null;
  endDate: Date | null = null;
  isLoading: boolean = false;
  gridDataComprehensive: any[] = [];
  gridDataCategory: any[] = [];
  gridDataLocation: any[] = [];
  gridDataUser: any[] = [];
  gridDataRisk: any[] = [];
  gridDataDetail: any[] = [];
  gridDataCriticalRisk: any[] = [];
  gridDataDepartment: any[] = [];
  gridDataComprehensiveInternal: any[] = [];
  gridDataCategoryInternal: any[] = [];
  gridDataLocationInternal: any[] = [];
  gridDataUserInternal: any[] = [];
  gridDataRiskInternal: any[] = [];
  gridDataDetailInternal: any[] = [];
  gridDataCriticalRiskInternal: any[] = [];
  gridDataDepartmentInternal: any[] = [];
  locationDropdownData: any[] = [];
  locationDropdownFields: Object = { text: "NAME", value: "ID" };
  selectedLocation: any = null;
  showLocationDropdown: boolean = false;
  clParameterValue: string = 'C';
  alertData = { alertMessage: '', alertVisible: '' };
  private alertCloseTimer: ReturnType<typeof setTimeout> | null = null;
  @Output() close = new EventEmitter<void>();
  flagfortwo = "True";
  compliance_type = "S";
  is_stat = 1;
  data: any;
  status_flag = "S";
  is_email_request_flag = false;
  report_type = "Detailed";
  start_date_detail = "01/01/2024";
  end_date_detail = "01/01/2025";
  public selectedStatus: 'S' | 'I' = 'S';
  public selectedTabIndex: number = 0;
  isDepartmentSummaryReportVisible!: boolean;
  customizationList: any;
  currentUserName: string = '';
  // location = "BITA Consulting, Pune";

  showViewTempDocuments: boolean = false;
  file_id: number = 0;
  doc_type: string = '';

  private subscription: Subscription = new Subscription();
  selectedTabIndex$: Observable<number>;
  isStateFresh$: Observable<boolean>;
  selectedLocation$: Observable<any>;
  locationDropdownData$: Observable<any[]>;
  showLocationDropdown$: Observable<boolean>;
  clParameterValue$: Observable<string>;
  locationState$: Observable<any>;
  startDate$: Observable<Date | null>;
  endDate$: Observable<Date | null>;
  dateState$: Observable<any>;
  cameViaWeeklyReportMail: boolean = false;
  hasDownloaded: boolean = false;
  customerId: string = '';
  userId: string = '';

  encryptedUserIdAfterOtpVerification: string = '';
  encryptedCustomerIdAfterOtpVerification: string = '';
  encryptedDateAfterOtpVerification: string = '';

  date: string = '';
  weeklyReportToken: string = '';
  decryptedCustomerId: number = 0;
  reportFileIds: { [key: string]: number } = {};
  reportFileIdsForInternal: { [key: string]: number } = {};
  decryptedUserId: number = 0;

  /**
   * Initializes the weekly report component and wires up store-based observables for report state, selected location, date range, and UI tab/dropdown data.
   * @example
   * new WeeklyReportComponent(standardreportservice, router, user, store, route)
   * void
   * @param {StandardReportService} standardreportservice - Service used to access standard report-related operations.
   * @param {Router} router - Angular router used for navigation.
   * @param {UserService} user - Service used to access user-related data and actions.
   * @param {Store} store - NgRx store used to select report state observables.
   * @param {ActivatedRoute} route - Current activated route information.
   * @returns {void} This constructor does not return a value.
   */
  constructor(
    private standardreportservice: StandardReportService,
    private router: Router,
    private user: UserService,
    private store: Store,
    private route: ActivatedRoute,
  ) {
    ({
      selectedTabIndex$: this.selectedTabIndex$,
      isStateFresh$: this.isStateFresh$,
      selectedLocation$: this.selectedLocation$,
      locationDropdownData$: this.locationDropdownData$,
      showLocationDropdown$: this.showLocationDropdown$,
      clParameterValue$: this.clParameterValue$,
      locationState$: this.locationState$,
      startDate$: this.startDate$,
      endDate$: this.endDate$,
      dateState$: this.dateState$,
    } = selectStandardReportStreams(this.store));
  }

  /**
  * Initializes weekly report state from session storage or route parameters, subscribes to tab and parameter updates, and triggers token generation.
  * @example
  * gOnInit()
  * void
  * @returns {void} No return value.
  */
  ngOnInit() {
    this.customizationList = sessionStorage.getItem('customizationList');
    this.isDepartmentSummaryReportVisible = this.customizationList?.includes('DeptSummarySTDReport') ?? false;

    this.encryptedUserIdAfterOtpVerification = sessionStorage.getItem('encryptedUserId') || '';
    this.encryptedCustomerIdAfterOtpVerification = sessionStorage.getItem('encryptedCustomerId') || '';
    this.encryptedDateAfterOtpVerification = sessionStorage.getItem('encryptedDate') || '';

    if (this.encryptedUserIdAfterOtpVerification !== '' && this.encryptedCustomerIdAfterOtpVerification !== '' && this.encryptedDateAfterOtpVerification !== '') {
      this.userId = this.encryptedUserIdAfterOtpVerification;
      this.customerId = this.encryptedCustomerIdAfterOtpVerification;
      this.date = this.encryptedDateAfterOtpVerification;
      this.cameViaWeeklyReportMail = true;
    } else {
      this.route.queryParams.subscribe(params => {
        this.customerId = params['cutomer_id'];
        this.userId = params['user_id'];
        this.date = params['date'];
        this.cameViaWeeklyReportMail = !!(this.customerId && this.userId && this.date);
      });
    }

    this.subscription.add(
      this.selectedTabIndex$.subscribe(tabIndex => {
        this.selectedTabIndex = tabIndex;
        this.selectedStatus = tabIndex === 1 ? 'I' : 'S';
      })
    );
    this.subscription.add(
      this.clParameterValue$.subscribe(value => {
        this.clParameterValue = value;
      })
    );
    this.generateTokenByUserIdForWeeklyReport();
  }

  /**
   * Generates a weekly report token for the current user and loads the related statutory and internal report data.
   * @example
   * generateTokenByUserIdForWeeklyReport()
   * undefined
   * @returns {void} No return value; updates component state and triggers data loading.
   **/
  generateTokenByUserIdForWeeklyReport() {
    const params = {
      user_id: this.userId,
      customer_id: this.customerId,
    }

    this.standardreportservice.generateTokenByUserIdForWeeklyReport(params).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.status === 200 && response.body?.success) {
          const { result } = response.body;
          this.weeklyReportToken = result.token;
          this.currentUserName = result.user_name || '';
          const decodedVerifier = atob(result.verifier);
          this.user.setVerifier(decodedVerifier);
          this.fetchGridDataForStatutory('ComprehensiveReport', 'Comprehensive');
          this.fetchGridDataForStatutory('CategoryReport', 'Category');
          this.fetchGridDataForStatutory('LocationReport', 'Location');
          this.fetchGridDataForStatutory('UserReport', 'User');
          this.fetchGridDataForStatutory('RiskReport', 'Risk');
          this.fetchGridDataForStatutory('DetailReport', 'Detail');
          this.fetchGridDataForStatutory('CriticalRiskReport', 'CriticalRisk');
          this.fetchGridDataForStatutory('DepartmentReport', 'Department');
          this.fetchGridDataForInternal('ComprehensiveReport', 'Comprehensive');
          this.fetchGridDataForInternal('CategoryReport', 'Category');
          this.fetchGridDataForInternal('LocationReport', 'Location');
          this.fetchGridDataForInternal('UserReport', 'User');
          this.fetchGridDataForInternal('RiskReport', 'Risk');
          this.fetchGridDataForInternal('DetailReport', 'Detail');
          this.fetchGridDataForInternal('CriticalRiskReport', 'CriticalRisk');
          this.fetchGridDataForInternal('DepartmentReport', 'Department');
        } else {
          console.error('Failed to generate token. Invalid response status or empty body');
        }
      },
      error: (error) => {
        console.error('Error generating token:', error);
      }
    }
    );
  }


  onTabSelected(e: any): void {
    const { selectedIndex } = e;
    this.store.dispatch(setStandardReportTab({ tabIndex: selectedIndex }));
    if (selectedIndex === 0) {
      this.selectedStatus = 'S';
    } else if (selectedIndex === 1) {
      this.selectedStatus = 'I';
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openReport(RType: string) {
    this.openReportInViewer(RType);
  }

  /**
   * Fetches statutory weekly report data, updates the appropriate grid dataset, and manages loading state.
   * @example
   * etchGridDataForStatutory('weekly', 'Comprehensive')
   * undefined
   * @param {string} reportFlag - Report flag used to request the weekly statutory report.
   * @param {string} target - Grid target name used to determine which dataset should be populated.
   * @returns {void} Does not return a value.
   **/
  fetchGridDataForStatutory(reportFlag: string, target: string) {
    this.fetchWeeklyGridData('S', reportFlag, this.reportFileIds, true, (resultArray) => {
      switch (target) {
        case 'Comprehensive':
          this.gridDataComprehensive = resultArray;
          break;
        case 'Category':
          this.gridDataCategory = resultArray;
          break;
        case 'Location':
          this.gridDataLocation = resultArray;
          break;
        case 'User':
          this.gridDataUser = resultArray;
          break;
        case 'Risk':
          this.gridDataRisk = resultArray;
          break;
        case 'Detail':
          this.gridDataDetail = resultArray;
          break;
        case 'CriticalRisk':
          this.gridDataCriticalRisk = resultArray;
          break;
        case 'Department':
          this.gridDataDepartment = resultArray;
          break;
      }
    });
  }

  /**
   * Requests one weekly report row and hands it to the caller's grid assignment, tracking loading state.
   * Shared by the statutory and internal fetches, which differ only in compliance type, file-id map and grid.
   * @param complianceType - `'S'` for statutory, `'I'` for internal.
   * @param reportFlag - Report flag used to request the weekly report.
   * @param fileIds - Map that receives the returned `file_id` for this report flag.
   * @param logInvalid - Whether to log an error when the response is unsuccessful or empty.
   * @param assignGrid - Receives the single result row wrapped in an array; assigns it to the target grid.
   * @returns void
   */
  // any: the weekly report row is an untyped API object.
  private fetchWeeklyGridData(
    complianceType: 'S' | 'I',
    reportFlag: string,
    fileIds: { [key: string]: number },
    logInvalid: boolean,
    assignGrid: (resultArray: any[]) => void
  ): void {
    this.isLoading = true;

    const params = buildWeeklyReportParams(complianceType, this.customerId, this.userId, reportFlag, this.date);

    this.standardreportservice.getWeeklyReportData(params, this.weeklyReportToken).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response.status === 200 && response.body?.success) {

          const resultObject = response.body.result;
          this.decryptedCustomerId = resultObject.customer_id;
          this.decryptedUserId = resultObject.user_id;
          if (resultObject?.file_id) {
            fileIds[reportFlag] = resultObject.file_id;
          }

          // Convert single object into array for grid
          assignGrid([resultObject]);
        } else if (logInvalid) {
          console.error('Invalid response status or empty body');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error(`Error fetching ${reportFlag}:`, error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Fetches weekly internal report data for a given report flag and assigns the result to the appropriate grid.
   * @example
   * etchGridDataForInternal('weekly', 'Comprehensive')
   * // populates gridDataComprehensiveInternal with the fetched report row
   * @param {string} reportFlag - The report flag used to request the specific weekly report.
   * @param {string} target - The grid target name indicating which internal data array to populate.
   * @returns {void} This method does not return a value.
   **/
  fetchGridDataForInternal(reportFlag: string, target: string) {
    this.fetchWeeklyGridData('I', reportFlag, this.reportFileIdsForInternal, false, (resultArray) => {
      switch (target) {
        case 'Comprehensive':
          this.gridDataComprehensiveInternal = resultArray;
          break;
        case 'Category':
          this.gridDataCategoryInternal = resultArray;
          break;
        case 'Location':
          this.gridDataLocationInternal = resultArray;
          break;
        case 'User':
          this.gridDataUserInternal = resultArray;
          break;
        case 'Risk':
          this.gridDataRiskInternal = resultArray;
          break;
        case 'Detail':
          this.gridDataDetailInternal = resultArray;
          break;
        case 'CriticalRisk':
          this.gridDataCriticalRiskInternal = resultArray;
          break;
        case 'Department':
          this.gridDataDepartmentInternal = resultArray;
          break;
      }
    });
  }

  /**
   * Formats a date as `dd-MM-yyyy` for the report API.
   * @param date - Date to format.
   * @returns The formatted date, or `''` when none is given.
   */
  formatDate(date: Date | null): string {
    return formatReportDate(date);
  }

  formatDateForApi(date: Date): string {
    return date.toISOString();
  }

  /**
   * Requests a standard report download for the current user and selected date/status filters.
   * @example
   * xportReportRequest(item, type)
   * void
   * @param {any} item - Unused item parameter passed to the request handler.
   * @param {string} type - The report type to request.
   * @returns {void} No value is returned.
   */
  exportReportRequest(item: any, type: string) {
    const params = requestedReportParams(this.user, {
      startDate: this.startDate,
      endDate: this.endDate,
      status: this.selectedStatus,
      type,
      location: ''
    });

    this.standardreportservice.downloadRequestedReport(params).subscribe({
      next: (response: HttpResponse<Blob>) => {
      },
      error: (error) => {
        console.error("Error downloading PDF report:", error);
      }
    });
    this.closeAlert();
  }

  /**
   * Downloads a weekly compliance report and saves it locally with an appropriate filename.
   * @example
   * downloadReport(item)
   * void
   * @param {any} item - Report metadata or report type used to request and name the downloaded file.
   * @returns {void} No return value; initiates a file download and updates component state.
   **/
  downloadReport(item: any) {
    this.hasDownloaded = true;
    // const params = {
    //   id: item.id || 0,
    //   user_id: this.user.getUserId(),
    //   customer_id: this.user.getCustomerId()
    // };

    const params = {
      user_id: this.userId,
      customer_id: this.customerId,
      compliance_type: this.selectedStatus,
      report_type: item,
      requested_date: this.date ? this.date : this.formatDate(new Date())
    };

    this.standardreportservice.downloadWeeklyReport(params, this.weeklyReportToken).subscribe({
      next: (response: HttpResponse<Blob>) => {
        saveReportDownload(response, item.pdf_name || 'document');
        this.hasDownloaded = false;
      },
      error: (error) => {
        this.hasDownloaded = false;
        if (this.alertCloseTimer) {
          clearTimeout(this.alertCloseTimer);
          this.alertCloseTimer = null;
        }
        const message = error.status === 404
          ? 'Report Not Found!'
          : 'Document could not be downloaded.';
        this.alertData = { alertMessage: message, alertVisible: 'error' };
      }
    });

    this.closeAlert();
  }

  /**
   * Opens a generated statutory weekly report ('STDR' flag) in the
   * DocumentUltimate viewer, using the identity decrypted from the
   * weekly-report link rather than the standard session.
   * @param reportType - Report flag whose generated file id was cached.
   */
  viewDoc(reportType: string) {
    const fileId = this.reportFileIds[reportType];
    if (!fileId) {
      if (this.alertCloseTimer) { clearTimeout(this.alertCloseTimer); this.alertCloseTimer = null; }
      this.alertData = { alertMessage: 'alerts.no_report_to_preview', alertVisible: 'error' };
      return;
    }

    this.file_id = fileId;
    this.doc_type = 'STDR';
    this.showViewTempDocuments = true;
  }

  /**
   * Opens a generated internal weekly report ('STDR' flag) in the
   * DocumentUltimate viewer, using the identity decrypted from the
   * weekly-report link rather than the standard session.
   * @param reportType - Report flag whose generated file id was cached.
   */
  viewDocForInternal(reportType: string) {
    const fileId = this.reportFileIdsForInternal[reportType];
    if (!fileId) {
      if (this.alertCloseTimer)
        { clearTimeout(this.alertCloseTimer); this.alertCloseTimer = null; }
      this.alertData = { alertMessage: 'alerts.no_report_to_preview', alertVisible: 'error' };
      return;
    }

    this.file_id = fileId;
    this.doc_type = 'STDR';
    this.showViewTempDocuments = true;
  }

  closeViewTempDocuments() {
    this.showViewTempDocuments = false;
  }

  /**
   * Opens the "show all" screen for a report.
   * @param reportFlag - Report flag.
   * @param complianceType - Compliance type flag.
   * @returns {void} Side effects: see {@link openShowAllStandardReport}.
   */
  showAllClick(reportFlag: string, complianceType: string) {
    openShowAllStandardReport(this.standardreportservice, this.router, reportFlag, complianceType);
  }

  confirmAlert(item?: any) {
    if (this.alertData.alertVisible === 'confirm') {
      this.exportReportRequest(item || {}, 'Detailed');
    } else {
      this.closeAlert();
    }
  }

  /**
   * Prompts for the detailed report: asks for both dates when one is missing (as an
   * `'error'` alert on this screen), otherwise asks to confirm the e-mailed export.
   * @returns {void} Side effect: sets `alertData`.
   */
  downloadDetailReport() {
    this.alertData = detailReportAlert(this.startDate, this.endDate, 'error');
  }

  closeAlert(): void {
    this.alertData.alertVisible = '';
    if (this.alertCloseTimer) clearTimeout(this.alertCloseTimer);
    this.alertCloseTimer = setTimeout(() => {
      this.alertData.alertMessage = '';
      this.close.emit();
      this.alertCloseTimer = null;
    }, 200);
  }

  onClikOpenButton(reportType?: string) {
    const RType = reportType || 'ComprehensiveReport';
    this.openReportInViewer(RType);
  }


 /**
  * Opens the selected report in the viewer by requesting authorized report data.
  * @example
  * penReportInViewer('Weekly Report')
  * void
  * @param {string} RType - The report type or name to load in the viewer.
  * @returns {void} No return value.
  */
 openReportInViewer(RType: string) {
  const params = {
    report_name: RType,
    customer_id: this.customerId,
    is_sat_or_int: this.selectedStatus,
    customer_level_location: this.clParameterValue,
  };

  this.isLoading = true;

  this.standardreportservice.getAuthorisedReportData(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
    next: (response) => this.handleReportViewerResponse(response, RType),
    error: (err) => this.handleReportViewerError(err)
  });
}

/**
* Handles the report viewer HTTP response, validates it, prepares navigation data, and redirects to the report viewer.
* @example
* handleReportViewerResponse(response, RType)
* undefined
* @param {HttpResponse<AuthorisedReportDataResponse>} response - The HTTP response received from the server.
* @param {string} RType - The report type identifier used to build the viewer state.
* @returns {void} No value is returned.
**/
private handleReportViewerResponse(response: HttpResponse<AuthorisedReportDataResponse>, RType: string) {
  this.isLoading = false;

  const result = resolveReportViewerConfig(response, RType);
  if (!result.ok) {
    this.setAlert(result.error, 'error');
    return;
  }
  const { cfg } = result;

  const { SD, ED } = this.resolvedDateRange();
  const navigationData = this.buildNavigationData(cfg, RType, SD, ED);

  sessionStorage.setItem('reportViewerState', JSON.stringify(navigationData));
  this.router.navigate(['/report-viewer'], { state: navigationData });
}

/**
 * Formats a Date or date-like string into a local `YYYY-MM-DD` string, returning an empty string for invalid or missing values.
 * @example
 * formatLocalDate(new Date('2024-01-05'))
 * '2024-01-05'
 * @param {Date | string | null | undefined} date - The date value to format.
 * @returns {string} The formatted local date string, or an empty string if the input is invalid.
 **/
private formatLocalDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else {
    d = new Date(date);
    if (isNaN(d.getTime())) return '';
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  // Optionally add time if needed: `${year}-${month}-${day}T00:00:00`
  return `${year}-${month}-${day}`;
}

/**
 * Resolves the report date range using the selected start and end dates, or defaults to the current fiscal period.
 * @example
 * resolvedDateRange()
 * { SD: "2024-04-01", ED: "2024-04-30" }
 * @returns {{ SD: string; ED: string }} An object containing the formatted start date (SD) and end date (ED).
 **/
private resolvedDateRange(): { SD: string; ED: string } {
  return resolveReportDateRange(this.startDate, this.endDate, d => this.formatLocalDate(d));
}

/**
 * Builds navigation data for the weekly report based on the current report configuration, dates, and selected location.
 * @example
 * buildNavigationData(cfg, RType, SD, ED)
 * { reportConfig: cfg, reportType: RType, reportCategory: selectedStatus, startDate: SD, endDate: ED, viewerParams: {...} }
 * @param {any} cfg - Report configuration object.
 * @param {string} RType - Report type identifier.
 * @param {string} SD - Start date string.
 * @param {string} ED - End date string.
 * @returns {any} Combined navigation payload including report settings and viewer parameters.
 **/
private buildNavigationData(cfg: any, RType: string, SD: string, ED: string): any {
  return buildReportViewerNavigation({
    cfg,
    reportType: RType,
    startDate: SD,
    endDate: ED,
    reportCategory: this.selectedStatus,
    clParameterValue: this.clParameterValue,
    showLocationDropdown: this.showLocationDropdown,
    selectedLocation: this.selectedLocation,
    user: {
      customerId: this.user.getCustomerId(),
      userId: this.user.getUserId(),
      customerName: this.user.getCustomerName(),
      role: this.user.getRole(),
    },
  });
}

private handleReportViewerError(err: any) {
  this.isLoading = false;
  console.error('API Error:', err);
  this.setAlert('Error loading report configuration.', 'error');
}

private setAlert(alertMessage: string, alertVisible: string) {
  this.alertData = { alertMessage, alertVisible };
}
}
